import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import ShareBar from '../components/ShareBar';
import SEO from '../components/SEO';
import Disclaimer from '../components/Disclaimer';

const AGE_GROUPS = [
  {
    label: 'Teens',
    range: '16–19',
    emoji: '🎒',
    color: '#67e8f9',
    ages: [16, 17, 18, 19],
    representativeAge: 18,
    headline: 'Just Starting Out',
    summary: 'Build your first savings habit, open a Roth IRA with earned income, and avoid debt.',
    target: '$1,000–$5,000',
    keyMove: 'Open a Roth IRA',
  },
  {
    label: 'Early 20s',
    range: '20–24',
    emoji: '🎓',
    color: '#38bdf8',
    ages: [20, 21, 22, 23, 24],
    representativeAge: 22,
    headline: 'Foundation Phase',
    summary: 'Build your emergency fund, get your employer 401(k) match, and minimize lifestyle inflation.',
    target: '$5,000–$15,000',
    keyMove: 'Max employer match',
  },
  {
    label: 'Late 20s',
    range: '25–29',
    emoji: '🚀',
    color: '#818cf8',
    ages: [25, 26, 27, 28, 29],
    representativeAge: 27,
    headline: 'Growth Phase',
    summary: 'Aim for 25–50% of your salary saved. Compound interest is working hard for you.',
    target: '$15,000–$50,000',
    keyMove: 'Max your Roth IRA',
  },
  {
    label: '30s',
    range: '30–39',
    emoji: '📈',
    color: '#a78bfa',
    ages: [30, 31, 32, 33, 34, 35, 36, 37, 38, 39],
    representativeAge: 30,
    headline: '1× Salary Milestone',
    summary: 'By 30: 1× salary saved. By 40: 3× salary. The 30s are where wealth gaps widen fast.',
    target: '$78k–$234k',
    keyMove: 'Hit 1× salary by 30',
  },
  {
    label: '40s',
    range: '40–49',
    emoji: '💼',
    color: '#34d399',
    ages: [40, 41, 42, 43, 44, 45, 46, 47, 48, 49],
    representativeAge: 45,
    headline: '3× Salary Milestone',
    summary: 'Target 3–6× salary saved. Catch-up contributions kick in at 50 — plan for them now.',
    target: '$234k–$468k',
    keyMove: 'Max 401(k) + IRA',
  },
  {
    label: '50s',
    range: '50–59',
    emoji: '⏳',
    color: '#fb923c',
    ages: [50, 51, 52, 53, 54, 55, 56, 57, 58, 59],
    representativeAge: 55,
    headline: '6× Salary Milestone',
    summary: 'Catch-up contributions available (+$7,500 to 401k, +$1,000 to IRA). Final sprint to retirement.',
    target: '$468k–$624k',
    keyMove: 'Max catch-up contributions',
  },
  {
    label: 'Pre-Retirement',
    range: '60–65',
    emoji: '🏁',
    color: '#22c55e',
    ages: [60, 61, 62, 63, 64, 65],
    representativeAge: 62,
    headline: '8–10× Salary Goal',
    summary: 'Medicare at 65, Social Security strategy matters. Target 8–10× salary before you retire.',
    target: '$624k–$780k',
    keyMove: 'Plan Social Security',
  },
  {
    label: 'Early Retirement',
    range: '66–70',
    emoji: '🌅',
    color: '#f59e0b',
    ages: [66, 67, 68, 69, 70],
    representativeAge: 67,
    headline: 'Social Security Sweet Spot',
    summary: 'Delay Social Security to 70 for max benefit (+32% vs. claiming at 62). Sustainable withdrawals are key.',
    target: '$780k–$840k',
    keyMove: 'Optimize SS timing',
  },
  {
    label: 'Retirement',
    range: '71–75',
    emoji: '🎯',
    color: '#f472b6',
    ages: [71, 72, 73, 74, 75],
    representativeAge: 73,
    headline: 'RMDs & Preservation',
    summary: 'RMDs begin at 73. Focus on sustainable withdrawals, estate planning, and tax efficiency.',
    target: 'Preserve 10× salary',
    keyMove: 'Manage RMDs at 73',
  },
];



export default function SavingsByAgeHub() {
  return (
    <div style={s.page}>
      <SEO
        title="How Much Should You Have Saved by Age? | KnowFi"
        description="Savings benchmarks by age group — teens, 20s, 30s, 40s, 50s, and retirement. See targets, median savings, and the one move to make at each life stage."
        path="/savings-by-age"
        keywords="how much should I have saved by age, savings by age, retirement savings benchmarks, savings goals by age, how much to save at 20, 30, 40, 50, 60, savings for teens, how much should a 30 year old have saved, how much should a 40 year old have saved, how much should a 50 year old have saved, Fidelity retirement milestones, net worth by age"
      />

      <div style={s.glow} />
      <div style={s.inner}>

        {/* Breadcrumb */}
        <div style={s.breadcrumb}>
          <Link to="/" style={s.breadLink}>Home</Link>
          <span style={s.breadSep}>›</span>
          <Link to="/blog?tab=data" style={s.breadLink}>Data & Tools</Link>
          <span style={s.breadSep}>›</span>
          <span style={s.breadCurrent}>Savings by Age</span>
        </div>

        {/* Header */}
        <div style={s.header}>
          <div style={s.badge}>📊 Data & Benchmarks</div>
          <h1 style={s.h1}>How Much Should You Have Saved?</h1>
          <p style={s.subtitle}>
            Real savings benchmarks for every stage of life — from first job to retirement.
            Pick your age group to see targets, where most Americans stand, and your single best next move.
          </p>
        </div>

        {/* Share this page */}
        <div style={{ margin: '0 0 24px' }}>
          <ShareBar text="How much should you have saved by age? Real benchmarks from teens to retirement — KnowFi" url="https://knowfi.io/savings-by-age" />
        </div>

        {/* Age group grid */}
        <div style={s.grid}>
          {AGE_GROUPS.map(group => (
            <Link key={group.label} to={`/savings-by-age/${group.representativeAge}`} style={s.card}>
              <div style={s.cardTop}>
                <span style={s.emoji}>{group.emoji}</span>
                <div>
                  <div style={{ ...s.groupLabel, color: group.color }}>{group.label}</div>
                  <div style={s.rangeText}>Ages {group.range}</div>
                </div>
              </div>
              <div style={s.headline}>{group.headline}</div>
              <p style={s.summary}>{group.summary}</p>
              <div style={s.cardFooter}>
                <div style={s.targetRow}>
                  <span style={s.targetLabel}>Target</span>
                  <span style={{ ...s.targetVal, color: group.color }}>{group.target}</span>
                </div>
                <div style={s.keyMoveRow}>
                  <span style={s.keyMoveLabel}>Key move</span>
                  <span style={s.keyMoveVal}>{group.keyMove}</span>
                </div>
              </div>
              <div style={{ ...s.viewLink, color: group.color, marginTop: '8px' }}>View benchmarks →</div>
              <div style={{ marginTop: '10px' }} onClick={e => e.preventDefault()}>
                <ShareBar
                  text={`Savings benchmarks for ${group.label} (ages ${group.range}) — target: ${group.target} | KnowFi`}
                  url={`https://knowfi.io/savings-by-age/${group.representativeAge}`}
                  compact
                />
              </div>
            </Link>
          ))}
        </div>

        {/* Fidelity reference table */}
        <div style={s.refSection}>
          <div style={s.refTitle}>📌 Fidelity Retirement Milestones</div>
          <div style={s.table}>
            {[
              { age: 30, multiple: '1×', target: '$78,000', link: 30 },
              { age: 40, multiple: '3×', target: '$234,000', link: 40 },
              { age: 50, multiple: '6×', target: '$468,000', link: 50 },
              { age: 60, multiple: '8×', target: '$624,000', link: 60 },
              { age: 67, multiple: '10×', target: '$780,000', link: 67 },
            ].map(({ age, multiple, target, link }, i) => (
              <div key={age} style={{ ...s.tableRow, ...(i === 4 ? { borderBottom: 'none' } : {}) }}>
                <div style={s.tableKey}>
                  <Link to={`/savings-by-age/${link}`} style={s.tableLink}>Age {age}</Link>
                </div>
                <div style={s.tableVal}>{multiple} annual salary</div>
                <div style={{ ...s.tableVal, color: '#38bdf8' }}>{target}</div>
              </div>
            ))}
          </div>
          <p style={s.refNote}>Based on a median US household income of ~$78,000. Adjust proportionally for your income.</p>
        </div>

        {/* Browse individual ages */}
        <div style={s.browseSection}>
          <div style={s.browseTitle}>Browse by Exact Age</div>
          <div style={s.ageGrid}>
            {Array.from({ length: 60 }, (_, i) => i + 16).map(age => (
              <Link key={age} to={`/savings-by-age/${age}`} style={s.ageChip}>
                {age}
              </Link>
            ))}
          </div>
        </div>

        {/* CTAs */}
        <div style={s.ctaSection}>
          <div style={s.ctaTitle}>📐 Build your personal money plan</div>
          <div style={s.ctaRow}>
            <Link to="/calculators" style={s.ctaBtn}>🔢 Retirement Calculator</Link>
            <Link to="/gameplan" style={s.ctaBtnSecondary}>🗺️ Get Your Game Plan</Link>
          </div>
        </div>

        <Disclaimer />
      </div>
    </div>
  );
}

const s = {
  page:     { background: '#0f172a', color: '#f8fafc', minHeight: '100vh', padding: '40px 24px 80px', position: 'relative', overflow: 'hidden' },
  glow:     { position: 'absolute', top: '-60px', left: '50%', transform: 'translateX(-50%)', width: '700px', height: '400px', background: 'radial-gradient(ellipse at center, rgba(56,189,248,0.06) 0%, transparent 65%)', pointerEvents: 'none' },
  inner:    { maxWidth: '900px', margin: '0 auto', position: 'relative', zIndex: 1 },

  breadcrumb:   { display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '32px' },
  breadLink:    { color: '#475569', textDecoration: 'none', fontSize: '13px' },
  breadSep:     { color: '#334155', fontSize: '13px' },
  breadCurrent: { color: '#64748b', fontSize: '13px' },

  header:   { marginBottom: '40px' },
  badge:    { display: 'inline-block', background: 'rgba(56,189,248,0.1)', border: '1px solid rgba(56,189,248,0.25)', color: '#38bdf8', fontSize: '12px', fontWeight: '700', padding: '4px 12px', borderRadius: '20px', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '14px' },
  h1:       { fontSize: 'clamp(24px, 4vw, 40px)', fontWeight: '900', margin: '0 0 14px', letterSpacing: '-0.5px', lineHeight: 1.2, color: '#f1f5f9' },
  subtitle: { fontSize: '17px', color: '#64748b', margin: 0, lineHeight: 1.65, maxWidth: '640px' },

  grid:       { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: '16px', marginBottom: '48px' },
  card:       { background: '#1e293b', borderRadius: '14px', padding: '22px', border: '1px solid #334155', textDecoration: 'none', color: '#f8fafc', display: 'flex', flexDirection: 'column', gap: '10px', transition: 'border-color 0.2s', cursor: 'pointer' },
  cardTop:    { display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '2px' },
  emoji:      { fontSize: '28px', lineHeight: 1 },
  groupLabel: { fontSize: '16px', fontWeight: '800', letterSpacing: '-0.3px' },
  rangeText:  { fontSize: '12px', color: '#475569', fontWeight: '500' },
  headline:   { fontSize: '14px', fontWeight: '700', color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.4px' },
  summary:    { fontSize: '14px', color: '#64748b', lineHeight: 1.65, margin: 0 },
  cardFooter: { background: '#0f172a', borderRadius: '8px', padding: '12px 14px', marginTop: '4px', display: 'flex', flexDirection: 'column', gap: '6px' },
  targetRow:  { display: 'flex', justifyContent: 'space-between', alignItems: 'center' },
  targetLabel:{ fontSize: '11px', color: '#475569', textTransform: 'uppercase', letterSpacing: '0.5px', fontWeight: '600' },
  targetVal:  { fontSize: '14px', fontWeight: '800' },
  keyMoveRow: { display: 'flex', justifyContent: 'space-between', alignItems: 'center' },
  keyMoveLabel:{ fontSize: '11px', color: '#475569', textTransform: 'uppercase', letterSpacing: '0.5px', fontWeight: '600' },
  keyMoveVal: { fontSize: '13px', color: '#94a3b8', fontWeight: '600', textAlign: 'right' },
  viewLink:   { fontSize: '13px', fontWeight: '700', marginTop: '4px' },

  refSection: { marginBottom: '40px' },
  refTitle:   { fontSize: '14px', fontWeight: '700', color: '#475569', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '12px' },
  table:      { background: '#1e293b', borderRadius: '12px', border: '1px solid #334155', overflow: 'hidden', marginBottom: '10px' },
  tableRow:   { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 18px', borderBottom: '1px solid #334155' },
  tableKey:   { fontSize: '14px', color: '#64748b', flex: 1 },
  tableVal:   { fontSize: '14px', fontWeight: '700', color: '#f1f5f9', minWidth: '120px', textAlign: 'right' },
  tableLink:  { color: '#64748b', textDecoration: 'none' },
  refNote:    { fontSize: '12px', color: '#475569', margin: 0, lineHeight: 1.6 },

  browseSection: { marginBottom: '40px' },
  browseTitle:   { fontSize: '14px', fontWeight: '700', color: '#475569', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '14px' },
  ageGrid:       { display: 'flex', flexWrap: 'wrap', gap: '8px' },
  ageChip:       { background: '#1e293b', border: '1px solid #334155', color: '#64748b', textDecoration: 'none', borderRadius: '8px', padding: '6px 12px', fontSize: '13px', fontWeight: '600', transition: 'all 0.15s' },

  ctaSection:      { background: 'rgba(56,189,248,0.04)', border: '1px solid rgba(56,189,248,0.15)', borderRadius: '14px', padding: '24px', textAlign: 'center', marginBottom: '32px' },
  ctaTitle:        { fontSize: '17px', fontWeight: '700', color: '#f1f5f9', marginBottom: '16px' },
  ctaRow:          { display: 'flex', gap: '12px', justifyContent: 'center', flexWrap: 'wrap' },
  ctaBtn:          { background: '#38bdf8', color: '#0f172a', padding: '12px 24px', borderRadius: '8px', textDecoration: 'none', fontWeight: '800', fontSize: '14px' },
  ctaBtnSecondary: { background: '#1e293b', color: '#f1f5f9', padding: '12px 24px', borderRadius: '8px', textDecoration: 'none', fontWeight: '700', fontSize: '14px', border: '1px solid #334155' },
};
